import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Bar } from 'react-chartjs-2';
import 'chart.js/auto';
import Modal from "../../components/Modal";

interface DashboardData {
  availableBalance: number;
  totalSales: number;
  totalOrders: number;
  totalVisitors: number;
  recentOrders: Order[];
  salesTrend: number[];
}

interface Order {
  id: number;
  date: string;
  customerName: string;
  amount: number;
  status: string;
}

const DashboardPage: React.FC = () => {
  const { t } = useTranslation();
  const [data, setData] = useState<DashboardData | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [filteredOrders, setFilteredOrders] = useState<Order[]>([]);
  const [sortType, setSortType] = useState<'date' | 'amount'>('date');

  const fetchDashboardData = async () => {
    try {
      const result: DashboardData = {
        availableBalance: 1200000,
        totalSales: 5000000,
        totalOrders: 250,
        totalVisitors: 1200,
        recentOrders: [
          { id: 1, date: '2024-10-10', customerName: '홍길동', amount: 50000, status: '배송중' },
          { id: 2, date: '2024-10-09', customerName: '김철수', amount: 30000, status: '배송완료' },
          { id: 3, date: '2024-10-08', customerName: '박영희', amount: 45000, status: '결제대기' },
        ],
        salesTrend: [500000, 600000, 700000, 800000, 650000, 700000, 750000],
      };

      setData(result);
      setFilteredOrders(result.recentOrders);
    } catch (error) {
      console.error('대시보드 데이터를 가져오는 데 실패했습니다:', error);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const handleSortChange = (type: 'date' | 'amount') => {
    setSortType(type);
    const sortedOrders = [...filteredOrders].sort((a, b) => {
      if (type === 'date') return new Date(b.date).getTime() - new Date(a.date).getTime();
      return b.amount - a.amount;
    });
    setFilteredOrders(sortedOrders);
  };

  if (!data) return <p>{t('setting:dashboard.loading')}</p>;

  const salesTrendData = {
    labels: ['월', '화', '수', '목', '금', '토', '일'],
    datasets: [
      {
        label: t('setting:dashboard.sales'),
        data: data.salesTrend,
        backgroundColor: 'rgba(34, 197, 94, 0.5)',
      },
    ],
  };

  const salesTrendOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top' as const },
      title: { display: false },
    },
    scales: {
      y: { beginAtZero: true },
    },
  };

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8 ">
        <div className="p-6 border border-[#f6f6f6] bg-white rounded-lg shadow">
          <h3 className="text-lg font-semibold mb-2">
            {t('setting:dashboard.available_balance')}
          </h3>
          <p className="text-2xl font-bold">
            ₩ {data.availableBalance.toLocaleString()}
          </p>
        </div>
        <div className="p-6 border border-[#f6f6f6] bg-white rounded-lg shadow">
          <h3 className="text-lg font-semibold mb-2">
            {t('setting:dashboard.total_sales')}
          </h3>
          <p className="text-2xl font-bold">
            ₩ {data.totalSales.toLocaleString()}
          </p>
        </div>
        <div className="p-6 border border-[#f6f6f6] bg-white rounded-lg shadow">
          <h3 className="text-lg font-semibold mb-2">
            {t('setting:dashboard.total_orders')}
          </h3>
          <p className="text-2xl font-bold">
            {data.totalOrders.toLocaleString()}
          </p>
        </div>
        <div className="p-6 border border-[#f6f6f6] bg-white rounded-lg shadow">
          <h3 className="text-lg font-semibold mb-2">
            {t('setting:dashboard.total_visitors')}
          </h3>
          <p className="text-2xl font-bold">
            {data.totalVisitors.toLocaleString()}
          </p>
        </div>
      </div>

      <div className="h-[500px] border border-[#f6f6f6] w-full bg-white p-6 rounded-lg shadow mb-8">
        <h3 className="text-lg font-semibold mb-4">
          {t('setting:dashboard.sales_trend')}
        </h3>
        <div className="h-full">
          <Bar data={salesTrendData} options={salesTrendOptions} />
        </div>
      </div>

      <div className="bg-white pt-4">
        <h3 className="text-lg font-semibold mb-4 flex justify-between items-center">
          {t('setting:dashboard.recent_orders')}
          <button
            onClick={openModal}
            className="text-blue-500 text-sm hover:underline"
          >
            {t('setting:dashboard.view_more')}
          </button>
        </h3>
        <table className="w-full table-auto ">
          <thead>
            <tr className="bg-[#f6f6f6] Pretendard-Regular tracking-wide text-[#545454]">
              <th className="px-4 py-2 text-left">
                {t('setting:dashboard.order_id')}
              </th>
              <th className="px-4 py-2 text-left">
                {t('setting:dashboard.date')}
              </th>
              <th className="px-4 py-2 text-left">
                {t('setting:dashboard.customer')}
              </th>
              <th className="px-4 py-2 text-left">
                {t('setting:dashboard.amount')}
              </th>
              <th className="px-4 py-2 text-left">
                {t('setting:dashboard.status')}
              </th>
            </tr>
          </thead>
          <tbody>
            {data.recentOrders.map((order) => (
              <tr key={order.id} className="border-b">
                <td className="px-4 py-2">{order.id}</td>
                <td className="px-4 py-2">{order.date}</td>
                <td className="px-4 py-2">{order.customerName}</td>
                <td className="px-4 py-2">₩ {order.amount.toLocaleString()}</td>
                <td className="px-4 py-2">{order.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal isOpen={isModalOpen} onClose={closeModal}>
        <div className="p-3">
          {' '}
          {/* 외부 padding을 넓게 조정 */}
          <h2 className="text-xl text-[#141414] Pretendard-Bold mb-4">
            {t('setting:dashboard.more_orders')}
          </h2>
          <div className="mb-6 flex flex-row-reverse gap-4 text-[#eaeaea]">
            {' '}
            {/* 간격 조정 */}
            <button
              onClick={() => handleSortChange('date')}
              className={`${
                sortType === 'date'
                  ? 'Pretendard-Bold text-[#545454]'
                  : 'text-[#757575] Pretendard-Regular '
              }`}
            >
              {t('setting:dashboard.sort_by_date')}
            </button>
            |
            <button
              onClick={() => handleSortChange('amount')}
              className={` ${
                sortType === 'amount'
                  ? 'Pretendard-Bold text-[#545454] '
                  : 'text-[#757575] Pretendard-Regular'
              }`}
            >
              {t('setting:dashboard.sort_by_amount')}
            </button>
          </div>
          <table className="w-full table-auto">
            <thead>
              <tr className="bg-[#f6f6f6] Pretendard-Regular tracking-wide text-[#545454]">
                <th className="px-6 py-4 text-left">
                  {t('setting:dashboard.order_id')}
                </th>{' '}
                {/* padding 조정 */}
                <th className="px-6 py-4 text-left">
                  {t('setting:dashboard.date')}
                </th>
                <th className="px-6 py-4 text-left">
                  {t('setting:dashboard.customer')}
                </th>
                <th className="px-6 py-4 text-left">
                  {t('setting:dashboard.amount')}
                </th>
                <th className="px-6 py-4 text-left">
                  {t('setting:dashboard.status')}
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map((order) => (
                <tr
                  key={order.id}
                  className="border-b text-[#141414] Pretendard-Medium tracking-wide"
                >
                  <td className="px-6 py-4">{order.id}</td> {/* padding 조정 */}
                  <td className="px-6 py-4">{order.date}</td>
                  <td className="px-6 py-4">{order.customerName}</td>
                  <td className="px-6 py-4">
                    ₩ {order.amount.toLocaleString()}
                  </td>
                  <td className="px-6 py-4">{order.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* <button onClick={closeModal} className="mt-4 text-blue-500 hover:underline">
            {t('setting:dashboard.close')}
          </button> */}
      </Modal>
    </div>
  );
};

export default DashboardPage;
